import { useState, type CSSProperties } from 'react';

import { Icon } from '../components/Icon.js';
import { RETENTION } from '../fixtures/config.js';
import type { Channel, ClosingVariant } from '../fixtures/domain.js';
import {
  deletionOf,
  deletionRemaining,
  deletionUrgency,
  isEnded,
  type DeletionUrgency,
} from '../fixtures/rules.js';
import { formatCountdown } from '../util/format.js';
import { useNow } from '../util/useNow.js';
import { AutoEndDialog } from './AutoEndDialog.js';
import styles from './DeletionCountdown.module.css';

/**
 * CSS Modules are typed as a string index, so `styles[urgency]` is unchecked in
 * both directions — renaming a DeletionUrgency member would silently yield
 * undefined and drop the styling. This map turns that into a compile error.
 */
const TONE: Record<DeletionUrgency, string> = {
  normal: styles.normal!,
  warning: styles.warning!,
  urgent: styles.urgent!,
};

interface DeletionCountdownProps {
  channel: Channel;
  counterpartName: string;
  /** Owned by the caller, which already routes reject vs close off the same rule. */
  variant: ClosingVariant;
  onEnd: (variant: ClosingVariant) => void;
  /** For a host that must know a dialog is up, e.g. the widget's Escape. */
  onOpenChange?: (open: boolean) => void;
}

/**
 * How long this chat has left, and a way to cut it short. Every conversation
 * sits on a deletion clock — usually the quiet 20-day inactivity sweep, but a
 * 30-minute fade once the counterpart reads a closing message — so the pill
 * doubles as the entry point for ending the chat deliberately.
 */
export function DeletionCountdown({
  channel,
  counterpartName,
  variant,
  onEnd,
  onOpenChange,
}: DeletionCountdownProps) {
  const [open, setOpenState] = useState(false);
  const setOpen = (next: boolean) => {
    setOpenState(next);
    onOpenChange?.(next);
  };
  const now = useNow();
  const deletion = deletionOf(channel, RETENTION, now);

  if (!deletion) return null;

  const label = formatCountdown(deletion.at, now);
  const ring = { '--remaining': `${deletionRemaining(deletion, now) * 100}%` } as CSSProperties;
  const ended = isEnded(channel);

  return (
    <>
      <button
        className={TONE[deletionUrgency(deletion, now)]}
        style={ring}
        aria-label={`Deletes in ${label}`}
        onClick={() => setOpen(true)}
      >
        {/* Same fate, different escapability: an open chat is on an idle timer
            any reply resets, an ended one is committed to a schedule. */}
        <Icon name={ended ? 'trash' : 'clockFading'} size={14} />
        <span className={styles.value}>{label}</span>
      </button>

      {open && (
        <AutoEndDialog
          deletion={deletion}
          now={now}
          counterpartName={counterpartName}
          ended={ended}
          variant={variant}
          onClose={() => setOpen(false)}
          onEnd={() => {
            setOpen(false);
            onEnd(variant);
          }}
        />
      )}
    </>
  );
}
